/**
 * Insider CTA: cart-add + auto-apply discount + open cart drawer (no navigation).
 *
 * MARKUP CONTRACT (paste in any page content - no <script> needed):
 *
 *   1) Once per page, declare the discount code in visible text:
 *        <span data-imn-promo>INSIDER15</span>
 *      The user can edit this text inline. Page editor preserves text content.
 *      Different pages = different codes. NOT hardcoded in JS.
 *
 *   2) One or more "Add to cart" buttons (can repeat anywhere on the page):
 *        <button type="button" class="om-insider-btn"
 *                data-variant-id="48703083774194">
 *          Add to Cart
 *        </button>
 *
 *   3) Optional cart-icon trigger (any clickable element opens the drawer):
 *        <a href="#" class="om-cart-trigger">
 *          <img src="..." alt="Open cart">
 *        </a>
 *
 * BEHAVIOR
 *   .om-insider-btn click:
 *     a) POST /cart/add.js with sections=cart-drawer,cart-icon-bubble
 *     b) Hand the response directly to <cart-drawer>.renderContents() (auto-opens)
 *     c) Background: GET /discount/<CODE> to set the Shopify discount cookie
 *        (fire-and-forget, never blocks/breaks the cart UX)
 *
 *   .om-cart-trigger click:
 *     - Just opens the cart drawer with current cart contents (no add).
 *
 * DISCOUNT SOURCE PRECEDENCE
 *   1. First [data-imn-promo] element's text content on the page (RECOMMENDED)
 *   2. Button's own data-discount attribute (legacy fallback)
 *   3. None (button still adds to cart, no discount applied)
 *
 * DESIGN NOTES (why this version is robust)
 *   - Event delegation on document so a single click handler binds for life,
 *     even when sections re-render and replace nodes.
 *   - Uses /cart/add.js response directly (has proper .sections shape).
 *     Does NOT refetch /?sections= - that endpoint returns a flat object that
 *     cart-drawer.js's renderContents silently rejects.
 *   - Discount cookie is fire-and-forget AFTER the drawer renders, so a bad
 *     code (or 4xx from Shopify) can never block subsequent add-to-cart calls.
 *   - Validates code shape (alphanumeric, dash, underscore) before firing
 *     /discount/<CODE> so we do not poison the cookie with junk.
 *   - try/finally always restores button state, so "stuck on Adding..." cannot
 *     leave the button disabled forever.
 *
 * Loaded from layout/landing.liquid. Editor strips inline <script>; this lives in /assets/.
 */
(function () {
  'use strict';

  // Edit-proof variant fallback (2026-09-02): Shopify's page editor strips
  // data-variant-id from body_html on every save, silently breaking the CTA.
  // This config lives in the theme (editor can't reach it) and resolves the
  // variant by page handle when the button's attribute is missing. The
  // attribute still wins when present, so other insider pages are unaffected.
  var VARIANT_BY_HANDLE = {
    'why-mineral-toothpastes-may-be-quietly-damaging-your-enamel': 48703083774194
  };
  function pageHandle() {
    var parts = (window.location.pathname || '').split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  }
  function resolveVariant(btn) {
    var v = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (v) return v;
    return VARIANT_BY_HANDLE[pageHandle()] || 0;
  }

  var BUSY = 'data-imn-busy';
  var DISCOUNT_PATTERN = /^[A-Za-z0-9_-]{2,64}$/;

  function getPagePromoCode() {
    var el = document.querySelector('[data-imn-promo]');
    if (!el) return '';
    return (el.textContent || '').trim();
  }

  function resolveDiscount(btn) {
    var pageCode = getPagePromoCode();
    if (pageCode) return pageCode;
    return (btn.getAttribute('data-discount') || '').trim();
  }

  /**
   * Sets the Shopify discount_code cookie via /discount/CODE?redirect=/cart.js.
   *
   * Returns a Promise that resolves when the cookie is set (after the full
   * redirect chain completes), or rejects on network error.
   *
   * Why fetch (not <img>): when the final response is text/javascript (cart.js)
   * an <img> request can be aborted before all redirects complete in some
   * browsers, leaving the cookie unset. fetch + redirect:'follow' walks the
   * full chain reliably and Set-Cookie is honored at each hop.
   *
   * Verified manually: GET /discount/INSIDER?redirect=/cart.js returns 302 with
   *   Set-Cookie: discount_code=INSIDER; path=/; SameSite=Lax
   * then 200 text/javascript.
   */
  function applyDiscountCookie(discount) {
    if (!discount) return Promise.resolve(false);
    if (!DISCOUNT_PATTERN.test(discount)) {
      console.warn('[insider-cta] discount "' + discount + '" looks malformed - skipping cookie set');
      return Promise.resolve(false);
    }
    return fetch('/discount/' + encodeURIComponent(discount) + '?redirect=/cart.js&_=' + Date.now(), {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'follow',
      cache: 'no-store',
      headers: { Accept: 'application/json, text/javascript, */*' }
    })
      .then(function (r) {
        console.log('[insider-cta] discount endpoint responded status=' + r.status + ' for code=' + discount);
        return r.status >= 200 && r.status < 400;
      })
      .catch(function (err) {
        console.warn('[insider-cta] discount apply error:', err);
        return false;
      });
  }

  function openDrawerWithData(data) {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return false;
    if (data && data.sections && typeof cartDrawer.renderContents === 'function') {
      try {
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (e) { /* CustomEvent unsupported - non-fatal */ }
      cartDrawer.renderContents(data);
      return true;
    }
    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return true;
    }
    return false;
  }

  function fallbackNavigate(discount) {
    var target = discount && DISCOUNT_PATTERN.test(discount)
      ? '/discount/' + encodeURIComponent(discount) + '?redirect=/cart'
      : '/cart';
    window.location.href = target;
  }

  function setBusy(btn, busy) {
    if (busy) {
      if (!btn.getAttribute('data-imn-original-html')) {
        btn.setAttribute('data-imn-original-html', btn.innerHTML);
      }
      btn.setAttribute(BUSY, '1');
      btn.disabled = true;
      btn.innerHTML = '<span class="om-insider-btn-line">Adding to cart\u2026</span>';
    } else {
      var original = btn.getAttribute('data-imn-original-html');
      if (original !== null) {
        btn.innerHTML = original;
        btn.removeAttribute('data-imn-original-html');
      }
      btn.removeAttribute(BUSY);
      btn.disabled = false;
    }
  }

  function handleAddClick(btn, e) {
    if (btn.getAttribute(BUSY) === '1') return;
    e.preventDefault();

    var variantId = resolveVariant(btn);
    if (!variantId) {
      console.error('[insider-cta] missing data-variant-id on button', btn);
      return;
    }
    var discount = resolveDiscount(btn);
    console.log('[insider-cta] add variant=' + variantId + ' discount=' + (discount || '(none)'));

    setBusy(btn, true);

    /* Fire BOTH requests in parallel so the discount cookie is set by the time
       the user could realistically click "Check out" in the drawer.  We still
       open the drawer as soon as cart-add returns (do not block on discount). */
    var addPromise = fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          var err = new Error(data && (data.description || data.message) || 'Add to cart failed (HTTP ' + r.status + ')');
          err.detail = data;
          throw err;
        }
        return data;
      });
    });

    var discountPromise = applyDiscountCookie(discount);

    addPromise
      .then(function (addData) {
        var opened = openDrawerWithData(addData);
        if (!opened) {
          /* Drawer unavailable - wait for cookie to set, then navigate so the
             discount is applied either way. */
          return discountPromise.then(function () { fallbackNavigate(discount); });
        }
        /* Drawer opened. Wait for discount in background just so we can log it. */
        discountPromise.then(function (ok) {
          if (ok) console.log('[insider-cta] discount cookie should now be set: ' + discount);
        });
      })
      .catch(function (err) {
        console.error('[insider-cta] add to cart failed:', err, err && err.detail);
        var msg = (err && err.message) || 'Sorry, we could not add this to your cart. Please refresh and try again.';
        alert(msg);
      })
      .then(function () {
        setBusy(btn, false);
      });
  }

  function handleCartTriggerClick(trigger, e) {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
    e.preventDefault();
    cartDrawer.open();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.om-insider-btn');
    if (btn) { handleAddClick(btn, e); return; }
    var trigger = e.target.closest && e.target.closest('.om-cart-trigger');
    if (trigger) { handleCartTriggerClick(trigger, e); return; }
  });
})();
